/**
 * Bill-source helpers for outlets.
 *
 * - Cashier portal (`manual`): OwnRewards Quick Bill records transactions.
 * - PetPooja / custom POS: bills arrive from the POS; Quick Bill is for
 *   coupon (and similar) validation only — not for creating bills.
 */

export function normalizePosKey(posName?: string | null): string {
  return String(posName || '').trim().toLowerCase();
}

/** Cashier portal / Manual Entry — Quick Bill creates transactions. */
export function isManualPos(posName?: string | null): boolean {
  const key = normalizePosKey(posName);
  return key === 'manual' || key === 'cashier' || key === 'cashier-portal';
}

export function isPetPoojaPos(posName?: string | null): boolean {
  return normalizePosKey(posName) === 'petpooja';
}

/** Custom / webhook POS (not PetPooja, not cashier). */
export function isCustomPos(posName?: string | null): boolean {
  const key = normalizePosKey(posName);
  if (!key || isManualPos(key) || isPetPoojaPos(key)) return false;
  return (
    key === 'others' ||
    key === 'customapp' ||
    key === 'custom-pos' ||
    key === 'custompos' ||
    key.startsWith('custom')
  );
}

/** Bills come from an external POS — do not record via Quick Bill. */
export function isIntegratedPos(posName?: string | null): boolean {
  return isPetPoojaPos(posName) || isCustomPos(posName);
}

/** Only cashier/manual outlets may create bills from Quick Bill. */
export function canRecordQuickBill(posName?: string | null): boolean {
  return isManualPos(posName);
}

/**
 * Paths filtered out for manual-POS outlets.
 * Products stays in the menu — catalog may be empty until POS sync.
 */
export const MANUAL_POS_HIDDEN_MENU_PATHS: readonly string[] = [];

export function filterMenuForPos<T extends { path: string }>(
  items: T[],
  posName?: string | null,
): T[] {
  if (!isManualPos(posName) || MANUAL_POS_HIDDEN_MENU_PATHS.length === 0) {
    return items;
  }
  const hidden = new Set(MANUAL_POS_HIDDEN_MENU_PATHS);
  return items.filter((item) => !hidden.has(item.path));
}
